'use client';

import React, { CSSProperties, SyntheticEvent } from 'react';

import { noop } from '@benzinga/utils';
import styled, { css, TC, ThemeContext } from '@benzinga/themetron';
import ReactDOM from 'react-dom';

import { faArrowsMaximize } from '@fortawesome/pro-regular-svg-icons';
import { faArrowsMinimize } from '@fortawesome/pro-regular-svg-icons';
import { Icon } from '../Icon';

import { Warning, Close, Error } from '@benzinga/themed-icons';

interface AlertSize {
  alert?: boolean;
}
interface CoverScreenSize {
  coverScreen?: boolean;
}
interface ManualSize {
  manual?: { height?: string; maxHeight?: string; maxWidth?: string; width?: string };
}

export interface ModalProps {
  className?: string | undefined;
  modalTitleBarClassName?: string;
  doNotCloseOnBackgroundClick?: boolean;
  crossBtn?: React.ReactElement;
  containerStyle?: CSSProperties;
  fullScreen?: boolean;
  disableFullScreenButton?: boolean;
  isError?: boolean;
  isWarning?: boolean;
  hideCrossBtn?: boolean;
  hideTitle?: boolean;
  size: ManualSize | CoverScreenSize | AlertSize;
  title?: string | React.ReactElement;
  onClose: () => void;
  positionUpperThird?: boolean;
  visible?: boolean;
  zIndex?: number;
  parentRef?: HTMLDivElement | null;
}

export const Modal: React.FC<React.PropsWithChildren<ModalProps>> = props => {
  const BACKGROUND = React.useRef<HTMLDivElement | null>(null);
  const theme = React.useContext(ThemeContext);
  const [portalHost, setPortalHost] = React.useState<HTMLDivElement | null>(null);
  const [isFullScreen, setIsFullscreen] = React.useState(false);
  const fullScreen = props.fullScreen || isFullScreen;
  const visible = props.visible ?? true;

  React.useEffect(() => {
    if (!(fullScreen && visible)) {
      setPortalHost(null);
      return noop;
    }

    const element = document.createElement('div');
    if (element.className.includes(`bz-theme-`)) {
      Array.from(element.classList)
        .filter(className => className.startsWith(`bz-theme-`))
        .forEach(className => element.classList.remove(className));
    }
    element.classList.add(`bz-theme-${theme.name}`);

    const preferredMount = document.getElementById('app-modal-element');
    const parent = preferredMount ?? document.body;
    parent.appendChild(element);
    setPortalHost(element);

    return () => {
      element.remove();
      setPortalHost(null);
    };
  }, [fullScreen, theme.name, visible]);

  const closeModal = React.useCallback(() => {
    const onClose = props.onClose;
    onClose && onClose();
  }, [props.onClose]);

  const backgroundClick = React.useCallback(
    (event: SyntheticEvent<HTMLElement>) => {
      event.stopPropagation();
      if (event.target === BACKGROUND.current && !props.doNotCloseOnBackgroundClick) {
        closeModal();
      }
    },
    [closeModal, props.doNotCloseOnBackgroundClick],
  );

  const title = props.title || '';
  let prefix: React.ReactElement | null = null;

  if (props.isError) {
    prefix = (
      <span className="u-warnText">
        <WarnTextError />
        Error:{' '}
      </span>
    );
  } else if (props.isWarning) {
    prefix = (
      <span className="u-accentText">
        <Warning />
        Error:{' '}
      </span>
    );
  }
  const handleCloseClick = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      closeModal();
    },
    [closeModal],
  );

  const CrossBtn = props.crossBtn || <CrossIcon className="u-closeIcon" onClick={handleCloseClick} />;

  const modal = (
    <ModalBackgroundDiv className={props.className} onClick={backgroundClick} ref={BACKGROUND}>
      <ModalContainerDiv
        $alert={(props.size as AlertSize).alert}
        $coverScreen={(props.size as CoverScreenSize).coverScreen}
        $manual={(props.size as ManualSize).manual}
        aria-labelledby="modalTitle"
        role="dialog"
        style={props.containerStyle}
      >
        {props.hideTitle || (
          <ModalTitleBarDiv className={props.modalTitleBarClassName}>
            <ModalTitleBarTitle id="modalTitle">
              {prefix}
              {title}
            </ModalTitleBarTitle>
            <TC.Row>
              {!props.disableFullScreenButton &&
                (props.fullScreen ||
                  (!fullScreen && (
                    <MaxMin icon={faArrowsMaximize} onClick={() => setIsFullscreen(true)} title="Maximize" />
                  )))}
              {!props.disableFullScreenButton &&
                (props.fullScreen ||
                  (fullScreen && (
                    <MaxMin icon={faArrowsMinimize} onClick={() => setIsFullscreen(false)} title="Minimize" />
                  )))}
              {props.hideCrossBtn || <CloseDiv>{CrossBtn}</CloseDiv>}
            </TC.Row>
          </ModalTitleBarDiv>
        )}
        <ScrollContainer>{props.children}</ScrollContainer>
      </ModalContainerDiv>
      {props.positionUpperThird && <div />}
    </ModalBackgroundDiv>
  );

  if (visible) {
    if (fullScreen) {
      if (!portalHost) {
        return null;
      }
      return ReactDOM.createPortal(<FullscreenWrapper zindex={props.zIndex}>{modal}</FullscreenWrapper>, portalHost);
    } else if (props.parentRef) {
      return ReactDOM.createPortal(modal, props.parentRef);
    } else {
      return modal;
    }
  } else {
    return null;
  }
};

const ModalBackgroundDiv = styled.div`
  background: ${props => `${props.theme.colors.background}55`};
  position: absolute;
  z-index: 200;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: space-around;
  align-items: center;
  flex-direction: column;
  overflow: hidden;
  backdrop-filter: blur(2px) saturate(40%);
`;

const ModalContainerDiv = styled.div<{
  $alert?: boolean;
  $coverScreen?: boolean;
  $manual?: { width?: string; height?: string; maxWidth?: string; maxHeight?: string };
}>`
  display: flex;
  flex-direction: column;
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.foreground};
  padding: 0;
  min-height: 1px;
  max-height: 100%;
  box-shadow: 0 0 10px ${props => `${props.theme.colorPalette.gray900}55`};

  ${props =>
    props.$alert
      ? css`
          width: 50%;
          max-width: 650px;
          min-width: 400px;
        `
      : css``}

  ${props =>
    props.$coverScreen
      ? css`
          height: 90vh;
          width: 80vw;
        `
      : css``}

  ${props =>
    props.$manual
      ? css`
          height: ${props.$manual.height ?? 'auto'};
          width: ${props.$manual.width ?? 'auto'};
          max-height: ${props.$manual.maxHeight ?? 'auto'};
          max-width: ${props.$manual.maxWidth ?? 'auto'};
        `
      : css``}
`;

const ModalTitleBarDiv = styled.div`
  display: flex;
  flex-shrink: 0;
  box-sizing: border-box;
  min-height: 26px;
  max-width: 100%;
  overflow: hidden;
  height: auto;
  background: ${props => props.theme.colors.backgroundActive};
  color: ${props => props.theme.colors.foregroundInactive};
  fill: ${props => props.theme.colors.backgroundActive};
  justify-content: space-between;
`;

const ModalTitleBarTitle = styled.span`
  flex-grow: 1;
  font-weight: 300;
  padding-top: 1px;
  line-height: 26px;
  max-width: 90%;
  font-size: 15px;
  text-overflow: ellipsis;
  &:first-child {
    padding-left: 6px;
  }
`;

const ScrollContainer = styled.span`
  overflow-y: auto;
  display: flex;
  flex: 1;
  flex-direction: column;
`;

const CrossIcon = styled(Close)`
  float: right;
  margin: auto;
`;

const MaxMin = styled(Icon)`
  align-self: center;
  display: block;
  margin: 0px 0px;
  svg {
    margin: 0px 4px;
    font-size: 1.15em;
    :hover {
      color: white;
    }
  }
`;

const CloseDiv = styled.div`
  align-self: center;
  display: block;
  margin: 0px 0px;
  cursor: pointer;
  svg {
    margin: 0px 4px;
    font-size: 1.5em;
    :hover {
      color: white;
    }
  }
`;

const FullscreenWrapper = styled.div<{
  zindex?: number;
}>`
  top: 0;
  left: 0;
  position: fixed;
  z-index: ${props => props.zindex ?? 50};
  width: 100%;
  height: 100%;
`;

const WarnTextError = styled(Error)`
  color: ${props => props.theme.colors.danger};
  fill: ${props => props.theme.colors.danger} !important;
`;
